/**
 * Shared merge utilities for the sync engine.
 *
 * These pure functions are the single source of truth for:
 * - Per-card Last-Write-Wins (LWW) conflict resolution
 * - Document-level LWW comparison
 * - Card diffing (detecting which cards changed between snapshots)
 * - Timestamp stamping (setting updatedAtISO on changed cards)
 *
 * Used by both the PartyServer (party/userdo.ts) and the client
 * (src/hooks/use-user-do-sync.ts). No browser or framework imports.
 */

import { hasCardChanged } from '../schema/card-sync-fields';
import type { CounterBag, StudyDay } from '../schema/entities';
import {
  QUIZ_ATTEMPT_TELEMETRY_KEYS,
  type SyncCardProps,
  type SyncQuizProgressData,
  type SyncQuizProgressEntry,
} from '../schema/shared-types';

// ---------------------------------------------------------------------------
// Card timestamp
// ---------------------------------------------------------------------------

/**
 * Canonical timestamp for a card, used in LWW comparisons.
 * Prefers `updatedAtISO`, falls back to `createdTimeISO`, then `""`.
 */
export function cardTimestamp(card: SyncCardProps): string {
  return card.updatedAtISO ?? card.createdTimeISO ?? '';
}

// ---------------------------------------------------------------------------
// Per-card LWW merge
// ---------------------------------------------------------------------------

/**
 * Merge two card arrays by ID using per-card Last-Write-Wins.
 *
 * - Cards only in `base` are kept.
 * - Cards only in `incoming` are added.
 * - Cards in both: the one with the newer `cardTimestamp()` wins.
 * - On tie (equal timestamps): if exactly one side has `deletedTimeISO`,
 *   the deleted side wins (tombstones are sticky on ties so a stale
 *   un-deleted replay can never accidentally resurrect a delete). If
 *   neither or both are deleted, `incoming` wins (most recent sender).
 *
 * Returns a new array — does not mutate inputs.
 */
export function mergeCardsLWW(
  base: SyncCardProps[],
  incoming: SyncCardProps[],
): SyncCardProps[] {
  const cardMap = new Map(base.map((c) => [c.id, c]));

  for (const card of incoming) {
    const prev = cardMap.get(card.id);
    if (!prev) {
      cardMap.set(card.id, card);
      continue;
    }
    const tsA = cardTimestamp(card);
    const tsB = cardTimestamp(prev);
    if (tsA > tsB) {
      cardMap.set(card.id, card);
    } else if (tsA === tsB) {
      // Tombstone-biased tiebreak: prefer the deleted side.
      if (card.deletedTimeISO && !prev.deletedTimeISO) {
        cardMap.set(card.id, card);
      } else if (!prev.deletedTimeISO || card.deletedTimeISO) {
        // Neither deleted, or both deleted → incoming wins on ties.
        cardMap.set(card.id, card);
      }
      // else: prev deleted, incoming not → keep prev (tombstone wins).
    }
  }

  return [...cardMap.values()];
}

// ---------------------------------------------------------------------------
// Generic per-item LWW merge (for `collection` entities)
// ---------------------------------------------------------------------------

/**
 * Merge two item arrays by primary key using per-item Last-Write-Wins.
 *
 * - Items only in `base` are kept.
 * - Items only in `incoming` are added.
 * - Items in both: the one with the newer `updatedAtISO` wins.
 *   On tie, `incoming` wins (the more recent sender).
 *
 * Works for any item type with a primary key and `updatedAtISO`.
 * Returns a new array — does not mutate inputs.
 */
export function mergeItemsLWW<T extends { updatedAtISO?: string }>(
  base: T[],
  incoming: T[],
  primaryKey: string & keyof T,
): T[];
export function mergeItemsLWW<T extends { updatedAtISO?: string }>(
  base: T[],
  incoming: T[],
  primaryKey: string,
): T[];
export function mergeItemsLWW<T extends { updatedAtISO?: string }>(
  base: T[],
  incoming: T[],
  primaryKey: string,
): T[] {
  const pk = primaryKey as keyof T;
  const map = new Map(base.map((item) => [item[pk], item]));

  for (const item of incoming) {
    const prev = map.get(item[pk]);
    if (!prev) {
      map.set(item[pk], item);
      continue;
    }
    const tsA = item.updatedAtISO ?? '';
    const tsB = prev.updatedAtISO ?? '';
    if (tsA > tsB) {
      map.set(item[pk], item);
    } else if (tsA === tsB) {
      // Tombstone-biased tiebreak. A deleted item at the same ts as a
      // non-deleted one should win — prevents stale un-deleted replays
      // from resurrecting a tombstone on equal-ts ties.
      const prevDeleted = (prev as { deletedTimeISO?: string | null }).deletedTimeISO;
      const itemDeleted = (item as { deletedTimeISO?: string | null }).deletedTimeISO;
      if (itemDeleted && !prevDeleted) {
        map.set(item[pk], item);
      } else if (!prevDeleted || itemDeleted) {
        // Neither deleted, or both deleted → incoming wins on ties.
        map.set(item[pk], item);
      }
      // else: prev deleted, incoming not → keep prev.
    }
  }

  return [...map.values()];
}

// ---------------------------------------------------------------------------
// Document-level LWW
// ---------------------------------------------------------------------------

/**
 * Returns `true` if `incomingTs` is newer than or equal to `existingTs`.
 * Ties go to incoming — matching `mergeCardsLWW` and `mergeItemsLWW`
 * tiebreaker semantics so the document-level gate doesn't reject a
 * batch whose per-item merge would have accepted it.
 *
 * If `existingTs` is undefined (first write), always returns `true`.
 */
export function isNewerDocument(
  existingTs: string | undefined,
  incomingTs: string,
): boolean {
  if (existingTs === undefined) return true;
  return incomingTs >= existingTs;
}

// ---------------------------------------------------------------------------
// Card diffing (for push detection)
// ---------------------------------------------------------------------------

/**
 * Compare two card snapshots and return cards from `curr` that differ
 * from their counterpart in `prev` (or are new).
 *
 * The synced fields are defined in `card-sync-fields.ts` — the single
 * source of truth for which fields trigger dirty detection.
 */
export function diffCards(prev: SyncCardProps[], curr: SyncCardProps[]): SyncCardProps[] {
  const prevMap = new Map(prev.map((c) => [c.id, c]));
  const changed: SyncCardProps[] = [];

  for (const card of curr) {
    const prevCard = prevMap.get(card.id);
    if (!prevCard || hasCardChanged(prevCard, card)) {
      changed.push(card);
    }
  }

  return changed;
}

// ---------------------------------------------------------------------------
// Stamp changed cards
// ---------------------------------------------------------------------------

/**
 * Set `updatedAtISO` to `now` on cards whose content changed compared
 * to `prev`. Cards that are unchanged keep their existing timestamp.
 * New cards (not in `prev`) are also stamped.
 *
 * The synced fields are defined in `card-sync-fields.ts` — the single
 * source of truth for which fields trigger timestamp updates.
 *
 * Returns a new array — does not mutate inputs.
 */
export function stampChangedCards(
  prev: SyncCardProps[],
  updated: SyncCardProps[],
  now: string,
): SyncCardProps[] {
  const prevMap = new Map(prev.map((c) => [c.id, c]));

  return updated.map((card) => {
    const prevCard = prevMap.get(card.id);
    if (!prevCard || hasCardChanged(prevCard, card)) {
      return { ...card, updatedAtISO: now };
    }
    // Unchanged — keep existing timestamp
    return card;
  });
}

// ---------------------------------------------------------------------------
// Quiz progress merge (array-length-wins — legacy)
// ---------------------------------------------------------------------------

/**
 * Merge two quiz attempt arrays. The longer array wins (more retakes =
 * more data). When lengths are equal, compare the `completedAt` timestamp
 * of the last entry; `incoming` wins ties.
 *
 * Returns one of the input arrays — does not allocate a new array.
 */
export function mergeQuizAttemptsLWW(
  base: SyncQuizProgressData[],
  incoming: SyncQuizProgressData[],
): SyncQuizProgressData[] {
  if (incoming.length > base.length) return incoming;
  if (base.length > incoming.length) return base;

  // Equal length — compare last completedAt (incoming wins ties)
  const baseLast = base[base.length - 1]?.completedAt ?? '';
  const incomingLast = incoming[incoming.length - 1]?.completedAt ?? '';
  return incomingLast >= baseLast ? incoming : base;
}

// ---------------------------------------------------------------------------
// Quiz progress merge (union-by-attempt-ts)
// ---------------------------------------------------------------------------

/**
 * Merge two quiz attempt arrays by union on `ts` (attempt ID).
 * Both arrays contribute all unique attempts; duplicates (same `ts`) are
 * deduped with incoming winning. Result is sorted by `ts` ascending.
 */
export function mergeQuizAttempts(
  base: SyncQuizProgressData[],
  incoming: SyncQuizProgressData[],
): SyncQuizProgressData[] {
  const byTs = new Map<string, SyncQuizProgressData>();
  for (const a of base) byTs.set(a.ts, a);
  for (const a of incoming) {
    const prior = byTs.get(a.ts);
    // Answers pair on the attempt as written: ids filled in from storage by
    // block can't tell two questions with the same text apart.
    byTs.set(a.ts, withStoredFields(withStoredQuestions(a, prior), prior, a));
  }
  return [...byTs.values()].sort((a, b) => a.ts.localeCompare(b.ts));
}

const CARRIED_ATTEMPT_KEYS = [
  ...QUIZ_ATTEMPT_TELEMETRY_KEYS,
  'quizSourceType',
  'quizSourceId',
] as const;

function withStoredFields(
  a: SyncQuizProgressData,
  prior: SyncQuizProgressData | undefined,
  written: SyncQuizProgressData,
): SyncQuizProgressData {
  if (!prior) return a;
  const carried = CARRIED_ATTEMPT_KEYS.filter(
    (k) => a[k] === undefined && prior[k] !== undefined,
  );
  return {
    ...a,
    ...Object.fromEntries(carried.map((k) => [k, prior[k]])),
    ...(Array.isArray(a.responses) && {
      responses: a.responses.map((slot, q) =>
        slot?.map((r, k) => {
          const p = prior.responses?.[priorSlot(written, prior, q)]?.[k];
          return r.ms === undefined && p?.ms !== undefined && p.text === r.text
            ? { ...r, ms: p.ms }
            : r;
        }),
      ),
    }),
  };
}

/**
 * Where question `q` of `a` sits in `prior`: by its id when that id is unique
 * on both sides, else as the same occurrence of its block (a report drops every copy
 * of its text, so twins keep their order). -1 when the stored copy no longer
 * has it. Pairing by position would shift after a report.
 */
function priorSlot(
  a: SyncQuizProgressData,
  prior: SyncQuizProgressData,
  q: number,
): number {
  const id = a.questions?.[q]?.id;
  const own = (a.questions ?? []).map((p) => p?.id);
  const ids = (prior.questions ?? []).map((p) => p?.id);
  const once = (list: unknown[]) => list.indexOf(id) === list.lastIndexOf(id);
  if (id && ids.includes(id) && once(own) && once(ids)) return ids.indexOf(id);
  if (!Array.isArray(a.quizzes) || !Array.isArray(prior.quizzes)) return q;
  const block = a.quizzes[q];
  let rank = a.quizzes.slice(0, q).filter((b) => b === block).length;
  for (let i = 0; i < prior.quizzes.length; i++) {
    if (prior.quizzes[i] === block && rank-- === 0) return i;
  }
  return -1;
}

/** An attempt written without `questions[]` keeps the twin already stored for the same blocks. */
function withStoredQuestions(
  a: SyncQuizProgressData,
  prior: SyncQuizProgressData | undefined,
): SyncQuizProgressData {
  if (a.questions || !prior?.questions) return a;
  const byBlock = new Map(
    prior.quizzes.map((block, i) => [block, prior.questions?.[i] ?? null]),
  );
  return {
    ...a,
    questions: a.quizzes.map((b) => byBlock.get(b) ?? null),
    formatVersion: 2,
  };
}

/**
 * Merge two quiz progress entries.
 *
 *  - Attempts are unioned by `ts` (see `mergeQuizAttempts`).
 *  - Envelope fields (`isFolderQuiz`, `userId`, `quizName`, `deckId`, …) come
 *    from whichever side wins LWW on `updatedAtISO`. Spreading `incoming`
 *    unconditionally would let an older replica overwrite identity-bearing
 *    fields on a newer one.
 *  - `deletedTimeISO` is a sticky tombstone: if either side has it set, the
 *    result keeps it (newer tombstone wins). This prevents a non-deleted
 *    replica from resurrecting an entry that was tombstoned elsewhere —
 *    locked in by the resurrection-prevention tests in
 *    `party/tests/sync-engine.test.ts`.
 */
export function mergeQuizEntry(
  base: SyncQuizProgressEntry,
  incoming: SyncQuizProgressEntry,
): SyncQuizProgressEntry {
  const attempts = mergeQuizAttempts(base.attempts, incoming.attempts);
  const incomingWins = (incoming.updatedAtISO ?? '') >= (base.updatedAtISO ?? '');
  const winner = incomingWins ? incoming : base;
  const loser = incomingWins ? base : incoming;
  const updatedAtISO = winner.updatedAtISO ?? new Date().toISOString();

  let deletedTimeISO: string | undefined;
  if (base.deletedTimeISO && incoming.deletedTimeISO) {
    deletedTimeISO =
      incoming.deletedTimeISO >= base.deletedTimeISO
        ? incoming.deletedTimeISO
        : base.deletedTimeISO;
  } else {
    deletedTimeISO = base.deletedTimeISO ?? incoming.deletedTimeISO;
  }

  return {
    ...winner,
    attempts,
    updatedAtISO,
    deletedTimeISO,
    // `examKind` and `restartOf` are sticky, like the tombstone.
    examKind: winner.examKind ?? loser.examKind,
    restartOf: winner.restartOf ?? loser.restartOf,
  };
}

// ---------------------------------------------------------------------------
// studyDays merge (per-session, per-field MAX — additive, offline-safe)
// ---------------------------------------------------------------------------

/** Per-field MAX of two counter bags. Idempotent + commutative. */
export function maxBag(a: CounterBag = {}, b: CounterBag = {}): CounterBag {
  const out: CounterBag = { ...a };
  for (const k in b) out[k] = Math.max(out[k] ?? 0, b[k] ?? 0);
  return out;
}

/**
 * Merge two `studyDays` entries. Each session slot is single-writer (one auth
 * session on one device), so per-session/per-field `max` converges without
 * losing or double-counting across retries, offline edits, or devices.
 */
export function mergeStudyDay(base: StudyDay, incoming: StudyDay): StudyDay {
  const sessions: StudyDay['sessions'] = { ...base.sessions };
  for (const sessionId in incoming.sessions) {
    sessions[sessionId] = maxBag(sessions[sessionId], incoming.sessions[sessionId]);
  }
  const incomingNewer = (incoming.updatedAtISO ?? '') >= (base.updatedAtISO ?? '');
  return {
    dateKey: base.dateKey,
    sessions,
    updatedAtISO: incomingNewer ? incoming.updatedAtISO : base.updatedAtISO,
  };
}

/**
 * `deckMindmaps` rows: last-write-wins, except that a save which carries no
 * `graph` never erases the stored one. A client from before the infinite
 * canvas writes only the v1 tree (`mindmap` + `layout`); replacing the row
 * whole would drop the canvas — zones, notes, pictures, styles, every main
 * node past the first — for good. The newer v1 is kept, the stored graph rides
 * along, and `graphStale` tells a canvas client to fold the v1 edits back into
 * it as it reads (`reconcileGraph`). A save WITH a graph is the whole map
 * again, flag cleared.
 */
export function mergeDeckMindmapEntry<
  T extends { updatedAtISO?: string; graph?: unknown; graphStale?: true },
>(existing: T, incoming: T): T {
  if ((existing.updatedAtISO ?? '') > (incoming.updatedAtISO ?? '')) return existing;
  if (incoming.graph !== undefined || existing.graph === undefined) return incoming;
  return { ...incoming, graph: existing.graph, graphStale: true };
}
